import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useRegisterRider, type RegisterRiderResult } from "@/lib/queries";
import { isValidUgandaPhone, normalizeUgandaPhone } from "@/lib/format";
import type { VehicleType } from "@/lib/types";
import { useUIStore } from "@/store/ui";

export function RegisterRiderSheet() {
  const open = useUIStore((s) => s.registerRiderOpen);
  const setOpen = useUIStore((s) => s.setRegisterRiderOpen);
  const register = useRegisterRider();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("+256");
  const [vehicleType, setVehicleType] = useState<VehicleType>("boda");
  const [plateNumber, setPlateNumber] = useState("");
  const [idPhoto, setIdPhoto] = useState<File | null>(null);
  const [created, setCreated] = useState<RegisterRiderResult | null>(null);

  function reset() {
    setName("");
    setPhone("+256");
    setVehicleType("boda");
    setPlateNumber("");
    setIdPhoto(null);
  }

  function close() {
    setCreated(null);
    setOpen(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return void toast.error("Full name is required");
    if (!isValidUgandaPhone(phone)) return void toast.error("Phone must be +256XXXXXXXXX");
    if (!plateNumber.trim()) return void toast.error("Plate number is required");
    if (idPhoto && (!idPhoto.type.startsWith("image/") || idPhoto.size >= 5 * 1024 * 1024)) {
      return void toast.error("ID photo must be an image smaller than 5 MB");
    }

    register.mutate(
      { name, phone: normalizeUgandaPhone(phone), vehicleType, plateNumber, idPhoto },
      {
        onSuccess: (result) => {
          if (idPhoto && !result.idPhotoUploaded) {
            toast.warning("Rider created, but the ID photo could not be uploaded");
          } else {
            toast.success("Rider registered — pending approval");
          }
          reset();
          setCreated(result);
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "Could not register rider"),
      },
    );
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
        else setOpen(true);
      }}
    >
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{created ? "Rider registered" : "Register new rider"}</SheetTitle>
          <SheetDescription>
            {created
              ? "The application is ready for document verification and approval."
              : "Creates a phone-based rider identity. Approval is required before they can work."}
          </SheetDescription>
        </SheetHeader>

        {created ? (
          <div className="space-y-4 px-4 pb-6">
            <div className="rounded-lg border border-border bg-secondary p-4">
              <dl className="space-y-4 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Application ID</dt>
                  <dd className="mt-1 break-all font-mono text-foreground">
                    {created.applicationId}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Status</dt>
                  <dd className="mt-1 font-medium capitalize text-foreground">
                    {created.status.replace("_", " ")}
                  </dd>
                </div>
              </dl>
            </div>
            <Button className="w-full" onClick={close}>
              Done
            </Button>
          </div>
        ) : (
          <form className="space-y-4 overflow-y-auto px-4 pb-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+256700000000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vehicle">Vehicle type</Label>
              <Select value={vehicleType} onValueChange={(v) => setVehicleType(v as VehicleType)}>
                <SelectTrigger id="vehicle" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="boda">Boda</SelectItem>
                  <SelectItem value="bicycle">Bicycle</SelectItem>
                  <SelectItem value="car">Car</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="plate">Plate number</Label>
              <Input
                id="plate"
                value={plateNumber}
                onChange={(e) => setPlateNumber(e.target.value)}
                placeholder="UAA 123A"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="idphoto">ID photo</Label>
              <Input
                id="idphoto"
                type="file"
                accept="image/*"
                onChange={(e) => setIdPhoto(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1" disabled={register.isPending}>
                {register.isPending ? "Registering…" : "Register rider"}
              </Button>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
